import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LockIcon, EyeIcon, EyeOffIcon, CheckIcon, XIcon } from 'lucide-react';
import { logger } from '@/utils/logger';
import { toAppError } from '@/types/errors';

export const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Password validation states
  const minLengthValid = password.length >= 8;
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  useEffect(() => {
    // Set page title
    document.title = 'Reset Password | Strykr.ai';
    
    // Verify that user has a valid session from the recovery link
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error('[ResetPassword] Error checking session:', error);
          throw error;
        }
        
        if (!session) {
          console.log('[ResetPassword] No valid session found, redirecting to forgot password');
          toast({
            variant: 'destructive',
            title: 'Invalid or expired reset link',
            description: 'Your password reset link has expired. Please request a new one.',
          });
          navigate('/auth?view=forgot_password', { replace: true });
        }
      } catch (error) {
        const appError = toAppError(error);
        logger.error('[ResetPassword] Session check error', appError);
        navigate('/auth?view=forgot_password', { replace: true });
      }
    };
    
    checkSession();
  }, [navigate, toast]);

  const handleResetPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validate passwords
    if (!minLengthValid) {
      toast({
        variant: 'destructive',
        title: 'Password too short',
        description: 'Password must be at least 8 characters long.',
      });
      return;
    }
    
    if (!passwordsMatch) {
      toast({
        variant: 'destructive',
        title: 'Passwords do not match',
        description: 'Please make sure both passwords match.',
      });
      return;
    }
    
    setIsLoading(true);

    try {
      logger.debug('[ResetPassword] Attempting to reset password');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session found');
      }

      // Update the user's password
      const { error } = await supabase.auth.updateUser({
        password,
      });

      if (error) throw error;

      logger.info('[ResetPassword] Password updated successfully');
      toast({
        title: 'Password updated successfully!',
        description: 'Your password has been updated. You can now sign in with your new password.',
      });

      // Redirect to sign in page after a short delay
      setTimeout(() => {
        navigate('/auth?view=sign_in', { replace: true });
      }, 2000);
      
    } catch (error) {
      const appError = toAppError(error);
      logger.error('[ResetPassword] Password reset error', appError);

      // Check if error is due to expired or invalid token
      const isExpiredOrInvalid = appError.message?.includes('invalid') || 
                                appError.message?.includes('expired');
      
      toast({
        variant: 'destructive',
        title: isExpiredOrInvalid ? 'Invalid or expired reset link' : 'Error updating password',
        description: isExpiredOrInvalid
          ? 'Your password reset link has expired or is invalid. Please request a new one.'
          : appError.message,
      });

      if (isExpiredOrInvalid) {
        navigate('/auth?view=forgot_password', { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex w-full h-[100dvh] items-center justify-center overflow-hidden px-4 sm:px-6 bg-gradient-to-br from-gray-900 via-blue-900/20 to-gray-900">
      {/* Background decorations */}
      <div 
        className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-10 pointer-events-none" 
        aria-hidden="true"
      />
      <div 
        className="absolute bottom-0 right-0 h-[28rem] w-[28rem] rounded-full bg-blue-500/10 blur-3xl pointer-events-none motion-reduce:opacity-0" 
        aria-hidden="true"
      />
      <div 
        className="absolute top-0 left-0 h-[28rem] w-[28rem] rounded-full bg-purple-500/10 blur-3xl pointer-events-none motion-reduce:opacity-0" 
        aria-hidden="true"
      />

      {/* Reset Password Form */}
      <div className="relative z-10 w-full max-w-md">
        <div className="bg-black/10 backdrop-blur-sm rounded-xl p-8 shadow-xl border border-gray-800/40">
          {/* Header */}
          <div className="space-y-2 text-center mb-8">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-100 to-white bg-clip-text text-transparent drop-shadow-sm">
              Reset Your Password
            </h1>
            <p className="text-sm text-blue-200/80 max-w-xs mx-auto">
              Enter your new password below
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="space-y-6">
            {/* New Password */}
            <div>
              <Label htmlFor="new-password" className="text-sm font-medium text-gray-200">
                New Password
              </Label>
              <div className="relative mt-2">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <LockIcon className="h-4 w-4 text-blue-400" />
                </div>
                <Input
                  id="new-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-10 pr-10 bg-gray-800/50 border-gray-700 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200"
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-300"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOffIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <Label htmlFor="confirm-password" className="text-sm font-medium text-gray-200">
                Confirm New Password
              </Label>
              <div className="relative mt-2">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <LockIcon className="h-4 w-4 text-blue-400" />
                </div>
                <Input
                  id="confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="pl-10 pr-10 bg-gray-800/50 border-gray-700 focus:border-blue-500 focus:ring-blue-500/20 transition-all duration-200"
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-300"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOffIcon className="h-4 w-4" />
                  ) : (
                    <EyeIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>

            {/* Password validation feedback */}
            {password.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-gray-400">Password requirements:</p>
                <div className="grid grid-cols-1 gap-1 text-xs">
                  <div className={`flex items-center gap-2 ${minLengthValid ? 'text-green-400' : 'text-red-400'}`}>
                    {minLengthValid ? <CheckIcon className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
                    At least 8 characters
                  </div>
                  <div className={`flex items-center gap-2 ${hasLowercase ? 'text-green-400' : 'text-red-400'}`}>
                    {hasLowercase ? <CheckIcon className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
                    Lowercase letter
                  </div>
                  <div className={`flex items-center gap-2 ${hasUppercase ? 'text-green-400' : 'text-red-400'}`}>
                    {hasUppercase ? <CheckIcon className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
                    Uppercase letter
                  </div>
                  <div className={`flex items-center gap-2 ${hasDigit ? 'text-green-400' : 'text-red-400'}`}>
                    {hasDigit ? <CheckIcon className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
                    Number
                  </div>
                </div>
                {confirmPassword.length > 0 && (
                  <div className={`flex items-center gap-2 ${passwordsMatch ? 'text-green-400' : 'text-red-400'}`}>
                    {passwordsMatch ? <CheckIcon className="h-3 w-3" /> : <XIcon className="h-3 w-3" />}
                    Passwords match
                  </div>
                )}
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={isLoading || !minLengthValid || !passwordsMatch}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-medium py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Updating Password...' : 'Update Password'}
            </Button>

            {/* Support Message */}
            <div className="text-center">
              <p className="text-xs text-gray-400">
                Having trouble?{' '}
                <a 
                  href="mailto:product@strykr.ai" 
                  className="text-blue-400 hover:text-blue-300 hover:underline"
                >
                  Contact support
                </a>
              </p>
            </div>

            {/* Back to sign in */}
            <div className="text-center">
              <a 
                href="/auth?view=sign_in" 
                className="text-sm text-blue-400 hover:text-blue-300 hover:underline"
              >
                Back to sign in
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 